import { useMemo } from 'react';

import { UTCDate } from '@date-fns/utc';
import { OrderSchema } from 'api/types/data';
import { OrderEntity } from 'api/types/entity';
import { OrderDetailsEntity } from 'api/types/entity/account/order/orderDetailsEntity';
import { convertDateByTimezone } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { DeepPartial } from 'types/deepPartial';

import { OrderDetailsFormType } from '../../common/forms/orderForm/type';
import { calculateBusinessDays } from '../../common/helpers';
import { OrderFormType } from '../forms/orderForm';

const parseDetails = (details: OrderDetailsEntity): OrderDetailsFormType => ({
  custodianOrganization: {
    value:
      details.custodianOrganization.referenceRecordId ||
      details.custodianOrganization.value,
    label: details.custodianOrganization.value,
    record: details.custodianOrganization.referenceRecordId
      ? { id: details.custodianOrganization.referenceRecordId }
      : undefined,
  },
  accountNumber: details.accountNumber,
  accountOwnerName: details.accountOwnerName!,
  brokerName: {
    value: details.brokerName.referenceRecordId || details.brokerName.value,
    label: details.brokerName.value,
    record: details.brokerName.referenceRecordId
      ? { id: details.brokerName.referenceRecordId }
      : undefined,
  },
  custodianOrganizationCode: details.custodianOrganizationCode,
});

export const useOrderInitialValues = (
  order: OrderEntity | null,
  orderSchema: OrderSchema | undefined,
  brokerTimezoneOffset: number,
  disabledDates: Date[]
) => {
  const { t } = useTranslation();

  const initialValues = useMemo((): DeepPartial<OrderFormType> | undefined => {
    if (!order || !orderSchema) return undefined;

    const startDate =
      order?.startDate ??
      (orderSchema
        ? convertDateByTimezone(
            new UTCDate(orderSchema.startDate),
            brokerTimezoneOffset
          )
        : undefined);

    const executionPeriodInBusinessDays =
      order.executionPeriodInBusinessDays !== undefined
        ? order.executionPeriodInBusinessDays
        : startDate
        ? calculateBusinessDays(startDate, order.endDate, disabledDates)
        : undefined;

    return {
      startDate,
      endDate: order.endDate,
      executionPeriodInBusinessDays,
      executionTerms: {
        orderType: order.executionTerms.orderType
          ? {
              value: order.executionTerms.orderType,
              label: t(
                `account.order.sections.executionTermsSection.fields.orderType.types.${order.executionTerms.orderType}`
              ),
            }
          : undefined,
        yield: order.executionTerms.yield ?? undefined,
        additionalNotes: order.executionTerms.additionalNotes ?? undefined,
        operationKind: order.executionTerms.operationKind
          ? {
              value: order.executionTerms.operationKind,
              label: t(
                `account.order.sections.executionTermsSection.fields.operationKind.types.${order.executionTerms.operationKind}`
              ),
            }
          : undefined,
        operationPurpose: order.executionTerms.operationPurpose ?? undefined,
      },
      financialInstrument: {
        isin: {
          value:
            order.financialInstrument.isin.referenceRecordId ||
            order.financialInstrument.isin.value,
          label: order.financialInstrument.isin.value,
          record: order.financialInstrument.isin.referenceRecordId
            ? { id: order.financialInstrument.isin.referenceRecordId }
            : undefined,
        },
        securityType: order.financialInstrument.securityType ?? undefined,
        issuer: order.financialInstrument.issuer ?? undefined,
        nominalValue: order.financialInstrument.nominalValue,
      },
      transactionVolume: order.transactionVolume
        ? {
            quantity: order.transactionVolume.quantity,
            unitPrice: order.transactionVolume.unitPrice,
            currency: {
              value: order.transactionVolume.currency!,
              label: order.transactionVolume.currency!,
            },
            transactionAmount: order.transactionVolume.transactionAmount,
          }
        : undefined,
      transferVolume: order.transferVolume
        ? {
            quantity: order.transferVolume.quantity,
            unitPrice: order.transferVolume.unitPrice,
            currency: order.transferVolume.currency
              ? {
                  value: order.transferVolume.currency,
                  label: order.transferVolume.currency,
                }
              : null,
            transactionAmount: order.transferVolume.transactionAmount,
          }
        : undefined,
      confirmationDocument: order.confirmationDocument
        ? {
            contractNumber:
              order.confirmationDocument?.contractNumber ?? undefined,
            contractDate: order.confirmationDocument?.contractDate ?? undefined,
            files: order.confirmationDocument?.files ?? [],
          }
        : undefined,
      senderDetails: order.senderDetails
        ? parseDetails(order.senderDetails)
        : undefined,
      recipientDetails: order.recipientDetails
        ? parseDetails(order.recipientDetails)
        : undefined,
      additionalParameters: {
        transactionDate:
          order.additionalParameters?.transactionDate ?? undefined,
        settlementDate: order.additionalParameters?.settlementDate ?? undefined,
      },
    };
  }, [brokerTimezoneOffset, disabledDates, order, orderSchema, t]);

  return initialValues;
};
